import { GovernanceColors } from '@/config/governanceColors';
import { useThreatGraphData } from '@/hooks/useCommandCenter';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Network, ShieldAlert, Layers, Users, GitMerge, Activity } from 'lucide-react';

// Cross-fleet threat-intelligence graph. The API scopes the response BY IDENTITY (see
// api/routes.js GET /threat-graph/stats): a deployment operator receives the fleet aggregate
// + moat metric; any other caller receives only their own tenant's posture. This view renders
// whichever shape it is handed, keyed on data.scope -- it does NOT itself gate the audience,
// because the boundary is enforced server-side. A customer literally cannot fetch the aggregate.

function StatCard({ icon: Icon, color, label, value, sub }: {
  icon: React.ElementType; color: string; label: string; value: React.ReactNode; sub?: string;
}) {
  return (
    <Card className="bg-[#292524] border-[#3A352F]">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon className="w-4 h-4" style={{ color }} />
          <span className="text-sm font-medium">{label}</span>
        </div>
        <p className="text-2xl font-semibold text-[#F5F0E8]" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
        {sub && <p className="text-xs text-[#A8A29E] mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function TopClasses({ rows }: { rows: Array<{ class: string; count: number }> }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-[#8F857B]">No attack classes recorded yet.</p>;
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.class} className="flex items-center gap-3">
          <span className="text-xs text-[#A8A29E] w-56 shrink-0 truncate" title={r.class}>{r.class}</span>
          <div className="flex-1 h-2 rounded bg-[#1C1917] overflow-hidden">
            <div className="h-full rounded" style={{ width: `${(r.count / max) * 100}%`, backgroundColor: GovernanceColors.primary }} />
          </div>
          <span className="text-xs text-[#F5F0E8] w-10 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
        </div>
      ))}
    </div>
  );
}

export function ThreatIntelligenceView() {
  const { data, loading } = useThreatGraphData();

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading Threat Intelligence data...</div>;
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <h2 className="text-lg font-semibold">Threat Intelligence</h2>
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-6 text-sm text-[#A8A29E]">
            Threat-intelligence graph is unavailable. Confirm the governance layer is running and
            you are authenticated.
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOperator = data.scope === 'operator';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Threat Intelligence</h2>
        <Badge
          variant="outline"
          style={{ color: GovernanceColors.primaryLight, borderColor: GovernanceColors.primaryLight }}
        >
          {isOperator ? 'Operator view -- fleet aggregate' : 'Your protection posture'}
        </Badge>
      </div>

      <p className="text-xs text-[#8F857B] max-w-3xl">
        Every flagged action, anomaly, and blocked attack becomes one anonymized signature -- the
        attack class plus a privacy-stripped pattern hash, never the payload. Signatures reused
        across more than one agent are the ones already hardening the rest of your fleet.
      </p>

      {isOperator ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Network} color={GovernanceColors.primary} label="Signature corpus" value={data.corpusSize ?? 0} sub={`${data.uniqueClasses ?? 0} distinct attack classes`} />
            <StatCard icon={GitMerge} color={GovernanceColors.streaming} label="Attacks prevented" value={data.attacksPrevented ?? 0} sub={`cross-agent stops from prior signatures; ${data.reusableSignatures ?? 0} reused across >1 agent`} />
            <StatCard icon={ShieldAlert} color={GovernanceColors.healthy} label="Attacks blocked" value={data.totalBlocked ?? 0} sub={`${data.totalObservations ?? 0} total observations`} />
            <StatCard icon={Users} color={GovernanceColors.primaryLight} label="Contributing tenants" value={data.tenantCount ?? 0} sub="feeding the shared graph" />
          </div>

          <Card className="bg-[#292524] border-[#3A352F]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <Layers className="w-4 h-4" style={{ color: GovernanceColors.primary }} />
                <span className="text-sm font-medium">Top attack classes (fleet-wide)</span>
              </div>
              <TopClasses rows={data.topClasses || []} />
            </CardContent>
          </Card>

          {data.moatMetricNote && (
            <p className="text-xs text-[#8F857B] italic">{data.moatMetricNote}</p>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Network} color={GovernanceColors.primary} label="Signatures protecting you" value={data.signaturesProtectingYou ?? 0} sub={`${data.attackClasses ?? 0} distinct attack classes`} />
            <StatCard icon={GitMerge} color={GovernanceColors.streaming} label="Attacks prevented" value={data.attacksPreventedForYou ?? 0} sub={`${data.crossAgentPatterns ?? 0} patterns reused across your agents`} />
            <StatCard icon={ShieldAlert} color={GovernanceColors.healthy} label="Attacks blocked" value={data.attacksBlocked ?? 0} sub="stopped pre-execution" />
            <StatCard icon={Activity} color={GovernanceColors.warning} label="Recent activity" value={(data.recent || []).length} sub="signatures updated recently" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-[#292524] border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Layers className="w-4 h-4" style={{ color: GovernanceColors.primary }} />
                  <span className="text-sm font-medium">Top attack classes</span>
                </div>
                <TopClasses rows={data.topClasses || []} />
              </CardContent>
            </Card>

            <Card className="bg-[#292524] border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="w-4 h-4" style={{ color: GovernanceColors.warning }} />
                  <span className="text-sm font-medium">Recent signatures</span>
                </div>
                {(data.recent && data.recent.length > 0) ? (
                  <div className="space-y-2">
                    {data.recent.map((r: { class: string; severity: string; count: number; lastSeen: string }, i: number) => (
                      <div key={`${r.class}-${i}`} className="flex items-center justify-between text-xs">
                        <span className="text-[#A8A29E] truncate mr-3" title={r.class}>{r.class}</span>
                        <div className="flex items-center gap-3 shrink-0">
                          <Badge
                            variant="outline"
                            style={r.severity === 'critical'
                              ? { color: GovernanceColors.critical, borderColor: GovernanceColors.critical }
                              : { color: '#A8A29E', borderColor: '#4A443D' }}
                          >
                            {r.severity}
                          </Badge>
                          <span className="text-[#F5F0E8] w-8 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#8F857B]">No signatures captured yet. As your agents run, blocked attacks and anomalies appear here.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
